import {effect, inject, Injectable, isDevMode, OnDestroy, signal, WritableSignal} from '@angular/core';
import {defer, Observable, of, Subscription, throwError, timer} from 'rxjs';
import {catchError, delay, mergeMap, switchMap} from 'rxjs/operators';
import {ResultParser} from './result-parser';
import {LoggerService} from '../logger.service';
import {AccessTokenService} from '../access-token.service';
import {CmsApiService} from '../cms-api.service';
import {JobStatusItem} from "../definitions/job-status-item";
import {OperationExecutionStatus} from "../definitions/operation-execution-status.enum";
import {IdleService} from '../../shared/idle.service';
import {HibernationFlowService} from '../../shared/hibernation-flow.service';
import {TranslateService} from '@ngx-translate/core';
import {PrimusBackendInstanceService} from '../primus-backend-instance.service';

@Injectable({
  providedIn: 'root'
})
export class JobStatusSubscriberService implements OnDestroy {
  // Injections
  private readonly accessTokenService: AccessTokenService = inject(AccessTokenService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly hibernationFlow: HibernationFlowService = inject(HibernationFlowService);
  private readonly idleService: IdleService = inject(IdleService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly primusBackendInstanceService: PrimusBackendInstanceService = inject(PrimusBackendInstanceService);
  private readonly translate: TranslateService = inject(TranslateService);

  public allJobs: WritableSignal<JobStatusItem[]> = signal([]);
  public finishedJobs: WritableSignal<JobStatusItem[]> = signal([]);
  public unhandledFinishedJobs: WritableSignal<JobStatusItem[]> = signal([]);
  public ongoingJobs: WritableSignal<JobStatusItem[]> = signal([]);
  public userData = null;

  public readonly autoStart: boolean = false;
  public parser = new ResultParser();

  private readonly refreshMs: number = 10000;
  private readonly maxRetries: number = 10000;
  private readonly retryDelayMs: number = 2000;

  private data$: Observable<any>; // Holds data fetched from the request.
  private waitForMessages = false;

  // Store all subscribed observables in this array,
  // in order to clean up correctly later!
  private subscriptions: Subscription[] = [];

  // All statuses that indicate that an operation is finished must be defined here
  private readonly finishedTypes = [
    OperationExecutionStatus.FINISHED.toString(),
    OperationExecutionStatus.CANCELLED.toString(),
    OperationExecutionStatus.STEP_COMPLETE.toString(),
    OperationExecutionStatus.OPERATION_COMPLETE.toString(),
    OperationExecutionStatus.DOWNLOADED.toString()
  ];

  constructor() {
    this.monitorIdleStatus();
    this.monitorHibernationStatus();
  }

  ngOnDestroy(): void {
    if (this.autoStart) {
      this.stopPolling();
    }
  }

  private handleReqError(error: any): void {
    if (!error) return;

    const status = error.status ?? 'unknown';
    const message = error?.error?.error ?? error?.error?.message ?? error?.message ?? error?.IMessage ?? 'Unknown error';
    this.logger.error(`[JOBSTATUS] Error Code: ${status}\nMessage: ${message}`);
  }

  private delayedRetry() {
    let retries = this.maxRetries;
    return (src: Observable<any>) =>
      src.pipe(
        mergeMap(error => retries-- > 0
          ? of(error).pipe(delay(this.retryDelayMs))
          : throwError(() => new Error('Max retries exceeded - giving up.')))
      );
  }

  private monitorIdleStatus() {
    effect(() => {
      this.idleService.idle() ? this.stopPolling() : this.startPolling();
    });
  }

  private monitorHibernationStatus() {
    effect(() => {
      // Pause polling while selector/wake/error flow is active.
      if (this.hibernationFlow.shouldPauseBackgroundPolling()) {
        this.stopPolling();
      } else {
        this.startPolling();
      }
    });
  }

  isWaitingForMessages(): boolean {
    return this.waitForMessages;
  }

  setWaitForMessages(waitForMessages: boolean) {
    this.waitForMessages = waitForMessages;
  }

  /**
   * Force an immediate jobstatus fetch, then keep polling. `startPolling`
   * on an already-running stream is a no-op that waits up to `refreshMs`
   * for the next tick - too slow for a job that must show in the
   * operations panel the moment it is queued (a short job can finish
   * before that tick). Restarting the stream fires its `timer(0, …)`
   * emission at once.
   */
  pollNow(): void {
    if (this.data$ || this.subscriptions.length > 0) {
      this.stopPolling();
    }
    this.startPolling(true);
  }

  startPolling(waitForMessages: boolean = false): void {
    this.setWaitForMessages(waitForMessages);

    // Keep at most one active polling stream.
    if (this.data$ || this.subscriptions.length > 0) {
      return;
    }

    const token = this.accessTokenService.getToken();

    if (!token) {
      this.stopPolling();
      return;
    }

    if (this.hibernationFlow.shouldSkipBackgroundPollingStart()) {
      // Skip polling while selector/error UI is active.
      this.stopPolling();
      return;
    }

    // Polling is meaningful only after API auth has completed for selected backend.
    // This avoids early jobstatus requests during selector/auth/wake transitions.
    if (!this.primusBackendInstanceService.isApiAuthenticated()) {
      this.stopPolling();
      return;
    }

    // Avoid throwing during early startup before an instance/API URL is selected.
    // If the backend URL isn't configured yet, just don't poll.
    if (!this.cms.getApiUrl('jobstatus/', true, true)) {
      this.stopPolling();
      return;
    }

    if (isDevMode()) {
      console.debug('[JOBSTATUS] -- start polling');
    }

    const fetchJobStatus$ = defer(() => this.cms.getJobStatus()).pipe(
      this.delayedRetry(),
      catchError((error) => {
        if (this.hibernationFlow.shouldSkipBackgroundPollingStart()) {
          this.stopPolling();
          return of({messages: []});
        }
        // Non-hibernation errors are handled as normal polling errors.
        if (!this.hibernationFlow.handleBackgroundPollingError(error?.status, 'job-status-polling-error')) {
          this.handleReqError(error);
          return of({messages: []});
        }
        // Hibernation-like polling error: wake in background and keep polling stream alive.
        return of({messages: []});
      })
    );

    this.data$ = timer(0, this.refreshMs).pipe(
      switchMap(() => fetchJobStatus$)
    );

    this.startMessageSubscription();
  }

  stopPolling(): void {
    for (const s of this.subscriptions) {
      s.unsubscribe();
    }
    this.subscriptions = [];
    this.data$ = null;

    if (isDevMode()) {
      console.debug('[JOBSTATUS] -- no active jobs, stopped polling');
    }
  }

  convertUTCDates(messages: JobStatusItem[]) {
    const dateStrToDateInts = (dateStr: string) => {
      if (!dateStr) return [];
      const [date, time] = dateStr.split(' ');
      const dateParts = date.split('.').map(Number);
      const timeParts = time.split(':').map(Number);
      return [...dateParts.reverse(), ...timeParts];
    };

    const dateTimeToUtc = (dateInts: number[]) => {
      return new Date(Date.UTC(dateInts[0], dateInts[1] - 1, dateInts[2], dateInts[3], dateInts[4], dateInts[5]));
    };

    const dateTimeToLocal = (dateStr: string) => {
      if (!dateStr) return '';
      const dateInts = dateStrToDateInts(dateStr);
      const utc = dateTimeToUtc(dateInts);
      return `${dateInts[2]}.${dateInts[1]}.${dateInts[0]} ${utc.toLocaleTimeString()}`;
    };

    return messages.map(m => ({
      ...m,
      started: dateTimeToLocal(m.started),
      endedDate: dateTimeToUtc(dateStrToDateInts(m.ended)),
      ended: dateTimeToLocal(m.ended),
      registered: dateTimeToLocal(m.registered)
    }));
  }

  hasStatus(statuses: string[], messages: JobStatusItem[], adminUser: boolean, currentUser: any): JobStatusItem {
    if (!messages || messages.length === 0) return null;

    let relevantMessages = messages;

    if (!adminUser && currentUser) {
      relevantMessages = messages.filter(message => message.created_by_id.replace('USER-', '') === currentUser.artifact_id.replace('USER-', ''));
    }

    return relevantMessages.find(m => statuses.includes(m.status)) || null;
  }

  getFinished(messages: JobStatusItem[], adminUser: boolean, currentUser: any): JobStatusItem {
    return this.hasStatus(this.finishedTypes, messages, adminUser, currentUser);
  }

  hasFinishedJobs(messages: JobStatusItem[], adminUser: boolean, currentUser: any) {
    return this.getFinished(messages, adminUser, currentUser) !== null;
  }

  setStatusType(jobStatusItem: JobStatusItem) {
    if (this.finishedTypes.includes(jobStatusItem.status)) {
      jobStatusItem.statusType = 'finished';
    } else {
      jobStatusItem.statusType = jobStatusItem.status === OperationExecutionStatus.FAILED || jobStatusItem.status === OperationExecutionStatus.NO_DATA ? 'failed' : 'active';
    }
  }

  private getFileTypeName(filetype: string): string {
    if (filetype) {
      switch (filetype) {
        case 'pdf':
          return ': PDF';

        case 'docx':
          return ': Word';

        case 'xls':
        case 'xlsx':
          return ': Excel';

        default:
          return ': Unknown';
      }
    }

    return '';
  }

  private getMetadata(job: JobStatusItem): string {
    const label = this.translate.instant('TRANS__JOB_STATUS__CREATED_BY');
    const registered = job.registered;

    if (job.fullname) {
      return `${label}: ${job.fullname} - ${registered}`;
    }

    return `${label}: ${registered}`
  }

  private getStatusType(status: string): string {
    if (this.finishedTypes.includes(status)) {
      return 'finished';
    }

    if (status === OperationExecutionStatus.FAILED || status === OperationExecutionStatus.NO_DATA) {
      return 'failed';
    }

    return 'active';
  }

  private handleJobData(jobs: JobStatusItem[]) {
    const allParsedJobs: JobStatusItem[] = [];
    const reportNames = new Map([
      ['inventory', 'TRANS__INVENTORY_REPORT__TITLE'],
      ['inventory_deleted', 'TRANS__ADVANCED_SEARCH__FILTER__DELETED_STATE__DELETED_ONLY'],
    ]);

    let key: string = '';
    let extraData: string = null;

    for (const job of jobs) {
      extraData = null;

      switch (job.status) {
        case ('download_pdf'): {
          key = 'TRANS__JOB_STATUS__CREATING_PDF';
          extraData = `: ${job.progress.toFixed(2)}%`;
          break;
        }

        case ('loading_artifact_events'): {
          key = 'TRANS__JOB_STATUS__LOADING_OBJECT_DETAILS';
          extraData = `: ${job.progress.toFixed(2)}%`;
          break;
        }

        case ('loading_data'): {
          key = 'TRANS__JOB_STATUS__LOADING_OBJECTS';
          extraData = `: ${job.progress.toFixed(2)}%`;
          break;
        }

        case ('parsing_objects'): {
          key = 'TRANS__JOB_STATUS__PARSING_OBJECTS';
          extraData = `: ${job.progress.toFixed(2)}%`;
          break;
        }

        case (job.status_message): {
          key = job.status_message;
          break;
        }

        case (OperationExecutionStatus.DOWNLOADED): {
          key = 'TRANS__JOB_STATUS__DOWNLOADED';
          break;
        }

        case (OperationExecutionStatus.FINISHED): {
          key = 'TRANS__JOB_STATUS__FINISHED';
          break;
        }

        case (OperationExecutionStatus.IN_PROGRESS):
        case (OperationExecutionStatus.RUNNING):{
          key = job.status_message;
          extraData = `: ${job.progress.toFixed(2)}%`;
          break;
        }

        case (OperationExecutionStatus.QUEUED):
        case (OperationExecutionStatus.REGISTERED): {
          key = 'TRANS__JOB_STATUS__QUEUED';
          break;
        }
        case (OperationExecutionStatus.STOPPED): {
          key = 'TRANS__JOB_STATUS__STOPPED';
          break;
        }
        case (OperationExecutionStatus.CANCELLED): {
          key = 'TRANS__JOB_STATUS__CANCELLED';
          break;
        }
        default: {
          key = job.status;
          break;
        }
      }

      allParsedJobs.push({
        ...job,
        fileTypeName: this.getFileTypeName(job.filetype),
        statusMessage: this.translate.instant(key ? key : 'TRANS__JOB_STATUS__QUEUED') + (extraData ?? ''),
        statusType: this.getStatusType(job.status),
        taskMetaData: this.getMetadata(job),
        task_name: job.task_type === 'reports'
          ? reportNames.get(job.report_type) ?? 'TRANS__JOB_TYPE_NAME__REPORT'
          : job.task_name
      });
    }

    this.allJobs.set(allParsedJobs);
    this.splitMessagesIntoSortedArrays();
  }

  private splitMessagesIntoSortedArrays() {
    const allUsersJobs = this.allJobs().filter(job => job.created_by_id.replace('USER-', '') === this.userData?.artifact_id.replace('USER-', ''));

    for (const job of allUsersJobs) {
      if (this.finishedTypes.includes(job.status) || job.status === OperationExecutionStatus.FAILED) {
        if (this.finishedJobs().findIndex(j => j.queue_id === job.queue_id) === -1) {
          this.finishedJobs.update(state => [...state, job]);
          this.unhandledFinishedJobs.update(state => [...state, job]);

          if (this.ongoingJobs().findIndex(j => j.queue_id === job.queue_id) !== -1) {
            this.ongoingJobs.update(state => {
              const index = state.findIndex(j => j.queue_id === job.queue_id);
              if (index !== -1) {
                state.splice(index, 1);
              }
              return state;
            });
          }
        }
      } else if (this.ongoingJobs().findIndex(j => j.queue_id === job.queue_id) === -1) {
        this.ongoingJobs.update(state => [...state, job]);
      }
    }
  }

  private startMessageSubscription() {
    this.subscriptions.push(
      this.data$.subscribe(d => {
        if (d?.messages?.length) {
          d.messages = this.convertUTCDates(d.messages);

          this.handleJobData(d.messages ?? []);

          this.parser.parse(d);

          if (d.messages.some((m: any) => this.finishedTypes.includes(m.status))) {
            if (!this.waitForMessages) {
              this.stopPolling();
            }
          }
        } else if (!this.waitForMessages) {
          this.stopPolling();
        }
      })
    );
  }
}
